// src/services/syncService.ts

/**
 * Servicio para manejar la sincronización de datos offline
 */

interface SyncResult {
    success: boolean;
    message: string;
    timestamp?: Date;
  }
  
  /**
   * Inicia la sincronización de datos pendientes
   * @returns Promise con el resultado de la operación
   */
  export const triggerSync = (): Promise<SyncResult> => {
    return new Promise((resolve) => {
      // Comprobar si el navegador está en línea
      if (!navigator.onLine) {
        resolve({
          success: false,
          message: "Sin conexión a internet. Se sincronizará automáticamente cuando vuelva la conexión."
        });
        return;
      }
  
      // Comprobar si el service worker está disponible
      if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
        resolve({
          success: false,
          message: "Service Worker no disponible. Intente recargar la aplicación."
        });
        return;
      }
  
      // Configurar un listener para la respuesta
      const messageListener = (event: MessageEvent) => {
        if (event.data && event.data.type === 'SYNC_COMPLETED') {
          navigator.serviceWorker.removeEventListener('message', messageListener);
          resolve({
            success: true,
            message: event.data.message || "Sincronización completada con éxito",
            timestamp: new Date()
          });
        }
      };
  
      // Registrar el listener
      navigator.serviceWorker.addEventListener('message', messageListener);
  
      // Establecer un timeout por si la respuesta nunca llega
      setTimeout(() => {
        navigator.serviceWorker.removeEventListener('message', messageListener);
        resolve({
          success: true,
          message: "Sincronización iniciada (sin confirmación)",
          timestamp: new Date()
        });
      }, 3000);
  
      // Enviar mensaje al Service Worker
      navigator.serviceWorker.controller.postMessage({
        type: 'SYNC_NOW'
      });
    });
  };
  
  /**
   * Comprueba si hay elementos pendientes de sincronización
   * @returns Promise con el número de elementos pendientes
   */
  export const getPendingSyncCount = async (): Promise<number> => {
    // Esta es una implementación simulada
    // En un caso real, consultarías tu almacenamiento IndexedDB o similar
  
    // Si estamos offline, simulamos algunos elementos pendientes
    if (!navigator.onLine) {
      // Para simular, asumimos que hay 1-3 elementos pendientes cuando estamos offline
      return Math.ceil(Math.random() * 3);
    }
  
    // Si estamos online pero el SW no está activo, no hay forma de saber
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      return 0;
    }
  
    // En una implementación real, aquí consultarías al SW o a IndexedDB
    // Por ahora devolvemos 0 para simular que no hay pendientes cuando hay conexión
    return 0;
  };
  
  /**
   * Registra un callback para ser notificado cuando se completa una sincronización
   * @param callback Función a llamar cuando se completa la sincronización
   * @returns Función para eliminar el listener
   */
  export const onSyncCompleted = (
    callback: (data: { message: string; timestamp: Date }) => void
  ): () => void => {
    if (!('serviceWorker' in navigator)) {
      return () => {}; // Noop function
    }
  
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'SYNC_COMPLETED') {
        callback({
          message: event.data.message || "Sincronización completada",
          timestamp: new Date()
        });
      }
    };
  
    navigator.serviceWorker.addEventListener('message', handleMessage);
  
    // Devolver función para eliminar el listener
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  };
  
  /**
   * Configura una sincronización automática cuando se recupera la conexión
   * @returns Función para limpiar los listeners
   */
  export const setupAutoSync = (): () => void => {
    const handleOnline = () => {
      console.log("Conexión recuperada, sincronizando automáticamente...");
      triggerSync();
    };
  
    window.addEventListener('online', handleOnline);
  
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  };
  
  export default {
    triggerSync,
    getPendingSyncCount,
    onSyncCompleted,
    setupAutoSync
  };